import apiClient, { buildAuthHeaders } from 'services/apiClient';

const RELEASE_NOTES_ENDPOINT = 'release-notes/api/v1/releases/';
const DEFAULT_FEED_LIMIT = 8;

const normalizeNumber = (value, fallback = 0) => {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : fallback;
};

const normalizeTag = (tag = {}) => ({
    name: tag.name || '',
    slug: tag.slug || '',
    color: tag.color || '',
});

const normalizeChangeItem = (item = {}) => ({
    id: item.id ?? '',
    category: item.category || '',
    categoryLabel: item.category_label || item.categoryLabel || item.category || '',
    title: item.title || '',
    description: item.description || '',
    sortOrder: normalizeNumber(item.sort_order ?? item.sortOrder),
});

const normalizeAttachment = (attachment = {}) => ({
    id: attachment.id ?? '',
    url: attachment.url || '',
    caption: attachment.caption || '',
    width: attachment.width ?? null,
    height: attachment.height ?? null,
    sortOrder: normalizeNumber(attachment.sort_order ?? attachment.sortOrder),
});

export const normalizeRelease = (release = {}) => ({
    id: release.id ?? '',
    slug: release.slug || '',
    version: release.version || '',
    title: release.title || '',
    summary: release.summary || '',
    releaseType: release.release_type || release.releaseType || '',
    releaseTypeLabel: release.release_type_label || release.releaseTypeLabel || release.release_type || release.releaseType || '',
    publishedAt: release.published_at || release.publishedAt || '',
    isPinned: Boolean(release.is_pinned ?? release.isPinned),
    tags: (release.tags || []).map(normalizeTag),
    itemCount: normalizeNumber(release.item_count ?? release.itemCount),
    primaryImage: release.primary_image || release.primaryImage || '',
    isRead: Boolean(release.is_read ?? release.isRead),
    items: (release.items || []).map(normalizeChangeItem),
    attachments: (release.attachments || []).map(normalizeAttachment),
    createdAt: release.created_at || release.createdAt || '',
});

export const normalizeReleaseFeed = (payload = {}) => ({
    results: (payload.results || []).map(normalizeRelease),
    hasMore: Boolean(payload.has_more ?? payload.hasMore),
    nextCursor: payload.next_cursor || payload.nextCursor || '',
    limit: normalizeNumber(payload.limit, DEFAULT_FEED_LIMIT),
});

const buildFeedEndpoint = ({ limit = DEFAULT_FEED_LIMIT, cursor = '' } = {}) => {
    const params = new URLSearchParams();
    params.set('limit', String(limit || DEFAULT_FEED_LIMIT));
    if (cursor) params.set('cursor', cursor);
    return `${RELEASE_NOTES_ENDPOINT}?${params.toString()}`;
};

export const getReleaseUnreadCount = async ({ token }) => {
    const response = await apiClient.get(`${RELEASE_NOTES_ENDPOINT}unread_count/`, {
        headers: buildAuthHeaders(token),
    });
    return normalizeNumber(response.data?.unread_count ?? response.data?.unreadCount);
};

export const getReleaseFeed = async ({ token, limit = DEFAULT_FEED_LIMIT, cursor = '' }) => {
    const response = await apiClient.get(buildFeedEndpoint({ limit, cursor }), {
        headers: buildAuthHeaders(token),
    });
    return normalizeReleaseFeed(response.data || {});
};

export const getReleaseDetail = async ({ token, slug }) => {
    const response = await apiClient.get(`${RELEASE_NOTES_ENDPOINT}${encodeURIComponent(slug)}/`, {
        headers: buildAuthHeaders(token),
    });
    return normalizeRelease(response.data || {});
};

export const markAllReleasesRead = async ({ token }) => {
    const response = await apiClient.post(`${RELEASE_NOTES_ENDPOINT}mark_all_read/`, {}, {
        headers: buildAuthHeaders(token),
    });
    return {
        markedRead: normalizeNumber(response.data?.marked_read ?? response.data?.markedRead),
        unreadCount: normalizeNumber(response.data?.unread_count ?? response.data?.unreadCount),
    };
};

export const markReleaseRead = async ({ token, slug }) => {
    const response = await apiClient.post(`${RELEASE_NOTES_ENDPOINT}${encodeURIComponent(slug)}/mark_read/`, {}, {
        headers: buildAuthHeaders(token),
    });
    return {
        detail: response.data?.detail || '',
        unreadCount: normalizeNumber(response.data?.unread_count ?? response.data?.unreadCount),
    };
};
